'use client';

import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { FOOTER_BADGES, MAILING_ADDRESS } from '@/data/flyerContent';

const Footer: React.FC = () => {
  return (
    <FooterWrapper>
      <div className="footer-container w-container">
        <div className="w-row">
          <div className="w-col w-col-2">
            <div className="column-div">
              <h4 className="footer-h1">Navigate</h4>
              <ul className="unordered-list">
                <li>
                  <Link href="/shop-by-vehicle" className="link">
                    Shop by Vehicle
                  </Link>
                </li>
                <li>
                  <Link href="/why-go-green" className="link">
                    Why Go Green?
                  </Link>
                </li>
                <li>
                  <Link href="/about-us" className="link">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link href="/faqs" className="link">
                    FAQs
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="w-col w-col-2">
            <div className="column-div">
              <h4 className="footer-h1">Guarantee</h4>
              <ul className="unordered-list">
                <li>
                  <Link href="/our-guarantee" className="link">
                    Warranty Info
                  </Link>
                </li>
                <li>
                  <Link href="/our-guarantee" className="link">
                    Returns
                  </Link>
                </li>
                <li>
                  <Link href="/cleaner-care" className="link">
                    Cleaner & Care
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="w-col w-col-2">
            <div className="column-div">
              <h4 className="footer-h1">Resources</h4>
              <ul className="unordered-list">
                <li>
                  <Link href="/private-label" className="link">
                    Private Label
                  </Link>
                </li>
                <li>
                  <Link href="/measure-filter" className="link">
                    How to Measure Filter
                  </Link>
                </li>
                <li>
                  <Link href="/careers" className="link">
                    Careers
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="w-col w-col-3">
            <div className="column-div">
              <h4 className="footer-h1">Contact Us</h4>
              <ul className="unordered-list">
                <li>
                  <a href="tel:+17244302050">+1 724.430.2050</a>
                </li>
                <li>
                  <a href="mailto:csr@greenfilter.com">csr@greenfilter.com</a>
                </li>
                <li>
                  <address className="footer-address">
                    {MAILING_ADDRESS.map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </address>
                </li>
              </ul>
            </div>
            <div>
              <a href="https://www.facebook.com/GreenFilterUSA/">
                <Image
                  src="/assets/facebook.webp"
                  alt="Facebook"
                  className="image-2"
                  width={32}
                  height={32}
                />
              </a>
              <a href="https://www.instagram.com/greenfilterusa/">
                <Image
                  src="/assets/instagram.webp"
                  alt="Instagram"
                  className="image-2"
                  width={32}
                  height={32}
                />
              </a>
              <a href="https://www.youtube.com/c/GreenFilter">
                <Image
                  src="/assets/youtube.webp"
                  alt="YouTube"
                  className="image-2"
                  width={32}
                  height={32}
                />
              </a>
            </div>
            <div className="footer-badges" aria-label="Memberships and Made in USA">
              {FOOTER_BADGES.map((b) => (
                <Image key={b.name} src={b.src} alt={b.name} title={b.name} width={56} height={56} />
              ))}
            </div>
          </div>
          <div className="w-col w-col-3">
            <div className="column-div">
              <h4 className="footer-h1">Join Our Mailing List</h4>
              <div className="form-block w-form">
                <form id="email-form" name="email-form" className="form">
                  <input
                    type="text"
                    className="w-input"
                    maxLength={256}
                    name="email-2"
                    placeholder="Email Address"
                    id="email-2"
                    required
                  />
                  <input
                    type="submit"
                    value="Submit"
                    className="submit-button green w-button"
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="credit-sec">
        <div className="footer-bottom-left">
          <Image
            src="/assets/gf-logo-registered.webp"
            alt="Green Filter® logo"
            className="footer-logo"
            width={141}
            height={26}
          />
          <span className="copyright">
            Copyright © {new Date().getFullYear()} Green Filter® USA. All Rights Reserved.
          </span>
          <Link href="/privacy-policy" className="link _15 grey">
            Privacy Policy
          </Link>
          <Link href="/sitemap" className="link _15 grey">
            Sitemap
          </Link>
        </div>
        <div className="footer-bottom-right">
          <div className="payment-icons">
            <Image
              src="/assets/payments/paypal.webp"
              alt="PayPal"
              className="payment-icon"
              width={60}
              height={36}
            />
            <Image
              src="/assets/payments/visa.webp"
              alt="Visa"
              className="payment-icon"
              width={60}
              height={36}
            />
            <Image
              src="/assets/payments/master.webp"
              alt="Mastercard"
              className="payment-icon"
              width={60}
              height={36}
            />
            <Image
              src="/assets/payments/express.webp"
              alt="American Express"
              className="payment-icon"
              width={60}
              height={36}
            />
          </div>
        </div>
      </div>
    </FooterWrapper>
  );
};

export default React.memo(Footer);

const FooterWrapper = styled.div`
  position: relative;
  padding: ${SPACING.section} 10% 0;
  background-color: ${COLORS.gray900};
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .w-container {
    margin-left: auto;
    margin-right: auto;
    max-width: 1280px;
  }
  .w-container:before,
  .w-container:after {
    content: ' ';
    display: table;
  }
  .footer-container {
    padding-top: 0;
    padding-bottom: 0;
  }

  .w-row:before,
  .w-row:after {
    content: ' ';
    display: table;
  }
  .w-col-2 {
    width: 16.66666667%;
  }
  .w-col-3 {
    width: 25%;
  }
  .w-col {
    position: relative;
    min-height: 1px;
    padding-left: ${SPACING.md};
    padding-right: ${SPACING.md};
    padding-bottom: 0;
    display: inline-flex;
    flex-direction: column;
  }
  .column-div {
    padding-right: 0px;
    padding-left: 0px;
    padding-bottom: 0px;
    text-align: left;
  }
  .footer-h1 {
    display: block;
    margin-bottom: ${SPACING.md};
    margin-top: 0;
    color: ${COLORS.white};
    text-align: left;
    ${TYPOGRAPHY.label}
    font-size: ${FONT_SIZE.body.xxs};
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }
  .unordered-list {
    color: ${COLORS.textMuted};
    text-align: left;
    padding-left: 0;
    list-style: none;
    margin-top: 0px;
    margin-bottom: 0;
    line-height: 1.8;
  }
  li {
    margin-bottom: 10px;
  }

  .unordered-list a {
    color: ${COLORS.textMuted};
    font-family: Lato, sans-serif;
  }

  .unordered-list a:hover {
    color: ${COLORS.brandGreen};
  }
  .link {
    ${TYPOGRAPHY.bodyXS}
    color: ${COLORS.textMuted};
    text-decoration: none;
    transition: color 0.2s ease;
  }
  .image-2 {
    width: 32px;
    height: 32px;
    margin-right: 16px;
    margin-top: 12px;
    opacity: 0.6;
    transition: all 0.3s ease;
  }

  .image-2:hover {
    opacity: 1;
    transform: translateY(-2px);
  }

  .form-block {
    margin-bottom: 0;
  }
  .form {
    margin-bottom: 0;
    text-align: left;
  }
  .w-input,
  .w-select {
    display: block;
    width: 100%;
    padding: ${SPACING.sm} ${SPACING.md};
    margin-bottom: ${SPACING.sm};
    ${TYPOGRAPHY.bodyXS}
    color: ${COLORS.white};
    vertical-align: middle;
    background-color: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  .w-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  .w-input:focus,
  .w-select:focus {
    border-color: rgba(0, 173, 35, 0.6);
    background-color: rgba(255, 255, 255, 0.08);
    outline: none;
  }
  .submit-button.green {
    background-color: ${COLORS.brandGreenAccessible};
    background-image: none;
    border-radius: 4px;
    -webkit-appearance: button;
    padding: ${SPACING.sm} ${SPACING.lg};
    ${TYPOGRAPHY.buttonSM}
    width: 100%;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .submit-button.green:hover {
    background-color: ${COLORS.brandGreenHover};
    transform: translateY(-1px);
  }

  .w-button {
    display: inline-block;
    padding: 9px 15px;
    background-color: #3898ec;
    color: white;
    border: 0;
    line-height: inherit;
    text-decoration: none;
    cursor: pointer;
    border-radius: 0;
  }

  .credit-sec {
    width: 100%;
    padding: ${SPACING.lg} 10%;
    margin-top: ${SPACING.sectionSmall};
    background-color: rgba(0, 0, 0, 0.4);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: ${SPACING.md};
  }

  .footer-bottom-left {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .footer-bottom-right {
    display: flex;
    align-items: center;
  }

  .footer-address {
    font-style: normal;
    line-height: 1.6;
  }

  .footer-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
  }

  .footer-badges img {
    width: 56px;
    height: 56px;
    object-fit: contain;
    border-radius: 4px;
  }

  .footer-logo {
    height: 26px;
    width: auto;
  }

  .payment-icons {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .payment-icon {
    height: 36px;
    width: auto;
    background: ${COLORS.white};
    padding: 8px 14px;
    border-radius: 4px;
    object-fit: contain;
  }

  .copyright {
    ${TYPOGRAPHY.bodyXS}
    color: ${COLORS.textMuted};
    white-space: nowrap;
  }

  .link._15.grey {
    ${TYPOGRAPHY.bodyXS}
    color: ${COLORS.textMuted};
    white-space: nowrap;
    text-decoration: none;
  }

  .link._15.grey:hover {
    color: ${COLORS.white};
    text-decoration: underline;
  }

  .link:hover {
    color: ${COLORS.brandGreen};
    text-decoration: none;
  }

  @media screen and (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.xxl} 5% 0;

    .w-row {
      display: flex;
      flex-direction: column;
    }

    .w-col-2,
    .w-col-3 {
      width: 100%;
      margin-bottom: 28px;
      padding-left: 0;
      padding-right: 0;
    }

    .column-div {
      text-align: left;
    }

    .footer-h1 {
      text-align: left;
      margin-bottom: 16px;
    }

    .unordered-list {
      text-align: left;
      margin-bottom: 0;
    }

    .credit-sec {
      padding: ${SPACING.lg} 5%;
      margin-top: ${SPACING.lg};
      flex-direction: column;
      gap: ${SPACING.md};
    }

    .footer-bottom-left {
      flex-direction: column;
      text-align: center;
      gap: ${SPACING.sm};
    }

    .footer-bottom-right {
      width: 100%;
      justify-content: center;
    }

    .payment-icons {
      flex-wrap: wrap;
      justify-content: center;
      gap: 8px;
    }

    .payment-icon {
      height: 30px;
      padding: 6px 12px;
    }

    .footer-logo {
      height: 30px;
    }

    .link._15.grey {
      font-size: 13px;
      color: ${COLORS.textMuted};
    }

    .copyright {
      font-size: 13px;
      color: ${COLORS.textMuted};
      white-space: normal;
      text-align: center;
    }

    .image-2 {
      margin-right: 12px;
      margin-left: 0;
      margin-top: 16px;
    }
  }

  @media screen and (max-width: 991px) {
    .w-container {
      max-width: 100%;
    }
  }

  @media screen and (min-width: 768px) and (max-width: 991px) {
    .w-col-2,
    .w-col-3 {
      width: 50%;
      margin-bottom: 30px;
    }
  }
`;
